import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../stores/authStore';
import AuthModal from './auth/AuthModal';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { Brain, User, LogOut, Rocket, Crown, Menu, BookOpen, Library } from 'lucide-react';

export default function Navigation() {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { user, signOut } = useAuthStore();
  const { plan } = useSubscriptionStore();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Zamknięcie menu mobilnego przy zmianie trasy
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = user
    ? [
        { text: 'Dashboard', href: '/dashboard' },
        { text: 'Moje kursy', href: '/moje-kursy' },
        { text: 'Profil', href: '/profil' },
      ]
    : [
        { text: 'O nas', href: '/o-nas' },
        { text: 'FAQ', href: '/faq' },
        { text: 'Blog', href: '/blog' },
      ];

  return (
    <>
      <nav className="bg-white/95 backdrop-blur-sm shadow-lg sticky top-0 z-50"> {/* Removed overflow-x-hidden */}
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Lewa strona nawigacji */}
            <div className="flex items-center space-x-8">
              <Link to="/" className="flex items-center space-x-2 transition-transform hover:scale-105">
                <Brain className="h-8 w-8 text-purple-600" />
                <span className="font-bold text-xl text-gray-900">Kariera z AI</span>
              </Link>
              <div className="hidden md:flex items-center space-x-6">
                <Link to="/biblioteka-kursow" className="flex items-center gap-2 text-gray-700 hover:text-purple-600 transition-colors">
                  <Library className="w-4 h-4" />
                  Biblioteka Kursów
                </Link>
                <Link to="/polityka-prywatnosci" className="text-gray-700 hover:text-purple-600 transition-colors">
                  Polityka prywatności
                </Link>
                <Link to="/o-nas" className="text-gray-700 hover:text-purple-600 transition-colors">
                  O nas
                </Link>
                <Link to="/faq" className="text-gray-700 hover:text-purple-600 transition-colors">
                  FAQ
                </Link>
                <Link to="/blog" className="text-gray-700 hover:text-purple-600 transition-colors">
                  Blog
                </Link>
                <Link to="/plany" className="flex items-center gap-2 text-gray-700 hover:text-purple-600 transition-colors">
                  <Crown className="w-4 h-4" />
                  Plany
                </Link>
              </div>
            </div>
            {/* Prawa strona nawigacji */}
            <div className="flex items-center gap-4">
              {/* Przycisk menu mobilnego */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-purple-50 transition-colors"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div className="flex items-center">
                {plan === 'premium' && (
                  <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-purple-100 rounded-full">
                    <Crown className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700">Premium</span>
                  </div>
                )}
                {user ? (
                  // Jeśli użytkownik jest zalogowany – pokazujemy przycisk profilu
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setShowDropdown(!showDropdown)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-700 hover:bg-purple-50 transition-colors group"
                    >
                      <User className="w-6 h-6 md:w-5 md:h-5" />
                      <div className="hidden md:block text-left">
                        <span className="block">Mój Profil</span>
                        <span className="text-xs text-gray-500 group-hover:text-purple-600 transition-colors truncate block max-w-[150px]">
                          {user.email}
                        </span>
                      </div>
                    </button>
                    <AnimatePresence>
                      {showDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          className="absolute right-0 mt-2  bg-white rounded-xl shadow-xl py-2 z-50 border border-gray-100"
                        >
                          <Link
                            to="/dashboard"
                            className="flex items-center gap-2 px-4 py-3 text-gray-700 hover:bg-purple-50 transition-colors"
                            onClick={() => setShowDropdown(false)}
                          >
                            <Rocket className="w-5 h-5 text-purple-600" />
                            <span>Twoja Ścieżka</span>
                          </Link>
                          <Link
                            to="/moje-kursy"
                            className="flex items-center gap-2 px-4 py-3 text-gray-700 hover:bg-purple-50 transition-colors"
                            onClick={() => setShowDropdown(false)}
                          >
                            <BookOpen className="w-5 h-5 text-purple-600" />
                            <span>Moje Kursy</span>
                          </Link>
                          <Link
                            to="/profil"
                            className="flex items-center gap-2 px-4 py-3 text-gray-700 hover:bg-purple-50 transition-colors"
                            onClick={() => setShowDropdown(false)}
                          >
                            <User className="w-5 h-5 text-purple-600" />
                            <span>Ustawienia profilu</span>
                          </Link>
                          <Link
                            to="/plany"
                            className="flex items-center gap-2 px-4 py-3 text-gray-700 hover:bg-purple-50 transition-colors"
                            onClick={() => setShowDropdown(false)}
                          >
                            <Crown className="w-5 h-5 text-purple-600" />
                            <span>Zmień Plan</span>
                          </Link>
                          <div className="h-px bg-gray-200 my-2" />
                          <button
                            onClick={() => {
                              setShowDropdown(false);
                              signOut();
                            }}
                            className="w-full flex items-center gap-2 px-4 py-3 text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <LogOut className="w-5 h-5" />
                            <span>Wyloguj się</span>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <>
                    {/* Wersja desktop: przycisk z ikoną i napisem */}
                    <div className="hidden md:block">
                      <button
                        onClick={() => setShowAuthModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-800 text-white rounded-lg hover:bg-purple-900 transition-colors"
                      >
                        <User className="w-5 h-5" />
                        <span>Dołącz za darmo</span>
                      </button>
                    </div>
                    {/* Wersja mobilna: kwadratowy przycisk tylko z ikoną */}
                    <div className="md:hidden">
                      <button
                        onClick={() => setShowAuthModal(true)}
                        className="flex items-center justify-center w-10 h-10 bg-purple-800 text-white rounded-lg hover:bg-purple-900 transition-colors"
                      >
                        <User className="w-5 h-5" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="fixed top-16 left-0 right-0 bg-white shadow-lg md:hidden z-50 max-h-[calc(100vh-4rem)] overflow-y-auto"
            >
              <div className="py-2 px-4 space-y-2">
                <Link
                  to="/biblioteka-kursow"
                  className="flex items-center gap-2 py-2 text-gray-700 hover:text-purple-600"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Library className="w-4 h-4" />
                  Biblioteka Kursów
                </Link>
                <Link
                  to="/polityka-prywatnosci"
                  className="block py-2 text-gray-700 hover:text-purple-600"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Polityka prywatności
                </Link>
                <Link
                  to="/o-nas"
                  className="block py-2 text-gray-700 hover:text-purple-600"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  O nas
                </Link>
                <Link
                  to="/faq"
                  className="block py-2 text-gray-700 hover:text-purple-600"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  FAQ
                </Link>
                <Link
                  to="/blog"
                  className="block py-2 text-gray-700 hover:text-purple-600"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Blog
                </Link>
                <Link
                  to="/plany"
                  className="flex items-center gap-2 py-2 text-gray-700 hover:text-purple-600"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Crown className="w-4 h-4" />
                  Plany
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
      {/* AuthModal został przeniesiony poza element <nav> */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </>
  );
}
